import {generatePath} from 'react-router';

import {TAutopaymentError} from 'src/containers/BalanceReplenishment';
import {EMessagingLimit, EQualityScore, EQualityStatus} from 'src/containers/Channels/definitions';
import {EWhatsAppCategory} from 'src/containers/MessageMatchers/WhatsAppForm/definitions';
import {EApproveStatus} from 'src/containers/MessageMatchers/definitions';
import PATHS from 'src/paths';

import {TGenericNotification} from './definitions';

enum EWebNotificationSection {
  NOTIFICATIONS = 'notifications',
  UPLOADS = 'uploads',
}

const UPLOADS_LIST_PATH = generatePath(PATHS.NOTIFICATIONS_TAB, {
  tab: EWebNotificationSection.UPLOADS,
});
const NOTIFICATIONS_LIST_PATH = generatePath(PATHS.NOTIFICATIONS_TAB, {
  tab: EWebNotificationSection.NOTIFICATIONS,
});

enum EWebNotificationType {
  SubscribersUploadSuccess = 'SubscribersUploadSuccess',
  SubscribersUploadPartial = 'SubscribersUploadPartial',
  SubscribersUploadFailed = 'SubscribersUploadFailed',
  SubscribersUploadLoading = 'SubscribersUploadLoading',

  BroadcastCompleted = 'BroadcastCompleted',
  BroadcastCancelled = 'BroadcastCancelled',

  LicenseExpiring = 'LicenseExpiring',

  CallbackUpdated = 'CallbackUpdated',

  ApikeyUserUpdated = 'ApikeyUserUpdated',

  CompanyDetailsUpdated = 'CompanyDetailsUpdated',
  PaymentDetailsUpdated = 'PaymentDetailsUpdated',
  TenantTariffAccepted = 'TenantTariffAccepted',
  AgreedChangeTariffRequest = 'AgreedChangeTariffRequest',
  NotAgreedChangeTariffRequest = 'NotAgreedChangeTariffRequest',
  JivoEnabled = 'JivoEnabled',
  JivoDisabled = 'JivoDisabled',

  MessageMatcherStatusChanged = 'MessageMatcherStatusChanged',
  MessageMatcherCategoryChanged = 'MessageMatcherCategoryChanged',

  InvoicePaid = 'InvoicePaid',
  StripeCheckout = 'StripeCheckout',
  PaykeeperPayment = 'PaykeeperPayment',

  ChannelProfilePartnerProfileCreated = 'ChannelProfilePartnerProfileCreated',
  ChannelProfileQualityScoreChanged = 'ChannelProfileQualityScoreChanged',
  ChannelProfileMessagingLimitChanged = 'ChannelProfileMessagingLimitChanged',
  ChannelProfileQualityStatusChanged = 'ChannelProfileQualityStatusChanged',
  ChannelProfileWABAViolation = 'ChannelProfileWABAViolation',
  ChannelProfileWABARestriction = 'ChannelProfileWABARestriction',
  ChannelProfileWABADisable = 'ChannelProfileWABADisable',
  ChannelProfileWABAReinstate = 'ChannelProfileWABAReinstate',
  ChannelProfileWABAMarketingMessagesApiTermsSigned = 'ChannelProfileWABAMarketingMessagesApiTermsSigned',

  ChannelProfileMaxBotRegistration = 'ChannelProfileMaxBotRegistration',
  ChannelProfileMaxBotSubjectCreated = 'ChannelProfileMaxBotSubjectCreated',

  MessageReportSuccess = 'MessageReportSuccess',
  MessageReportFailed = 'MessageReportFailed',
  MessageReportLoading = 'MessageReportLoading',

  FlowMessageReportSuccess = 'FlowMessageReportSuccess',
  FlowMessageReportFailed = 'FlowMessageReportFailed',
  FlowMessageReportLoading = 'FlowMessageReportLoading',

  ChannelProfileMaxConnected = 'ChannelProfileMaxConnected',
  ChannelProfileMaxDisconnected = 'ChannelProfileMaxDisconnected',

  ChannelProfileTelegramConnected = 'ChannelProfileTelegramConnected',
  ChannelProfileTelegramDisconnected = 'ChannelProfileTelegramDisconnected',
}

const UPLOADS_TYPES = [
  EWebNotificationType.SubscribersUploadFailed,
  EWebNotificationType.SubscribersUploadSuccess,
  EWebNotificationType.SubscribersUploadPartial,
  EWebNotificationType.SubscribersUploadLoading,
  EWebNotificationType.MessageReportLoading,
  EWebNotificationType.MessageReportSuccess,
  EWebNotificationType.MessageReportFailed,
  EWebNotificationType.FlowMessageReportLoading,
  EWebNotificationType.FlowMessageReportSuccess,
  EWebNotificationType.FlowMessageReportFailed,
];

const HIDDEN_TYPES = [
  EWebNotificationType.SubscribersUploadLoading,
  EWebNotificationType.MessageReportLoading,
  EWebNotificationType.FlowMessageReportLoading,
  EWebNotificationType.AgreedChangeTariffRequest,
  EWebNotificationType.NotAgreedChangeTariffRequest,
];

type TChannelProfilePartnerProfileCreated = TGenericNotification<
  EWebNotificationType.ChannelProfilePartnerProfileCreated,
  {
    name: string;
  }
>;

type TChannelProfileQualityStatusChangedNotification = TGenericNotification<
  EWebNotificationType.ChannelProfileQualityStatusChanged,
  {
    whatsAppProfileQualityStatusChanged: {
      name: string;
      qualityStatus: EQualityStatus;
    };
  }
>;

type TChannelProfileMessagingLimitChangedNotification = TGenericNotification<
  EWebNotificationType.ChannelProfileMessagingLimitChanged,
  {
    whatsAppProfileMessagingLimitChanged: {
      name: string;
      messagingLimit: EMessagingLimit;
    };
  }
>;

type TChannelProfileQualityScoreChangedNotification = TGenericNotification<
  EWebNotificationType.ChannelProfileQualityScoreChanged,
  {
    whatsAppProfileQualityScoreChanged: {
      name: string;
      qualityScore: EQualityScore;
    };
  }
>;

type TChannelProfileViolationNotification = TGenericNotification<
  EWebNotificationType.ChannelProfileWABAViolation,
  {
    whatsAppESProfileWABAViolation: {
      wabaId: number;
      violationType: string;
      whatsappEsProfileInternalNames: string[];
    };
  }
>;

type TChannelProfileRestrictionNotification = TGenericNotification<
  EWebNotificationType.ChannelProfileWABARestriction,
  {
    whatsAppESProfileWABARestriction: {
      wabaId: number;
      expirationDate: string;
      whatsappEsProfileInternalNames: string[];
    };
  }
>;

type TChannelProfileDisableNotification = TGenericNotification<
  EWebNotificationType.ChannelProfileWABADisable,
  {
    whatsAppESProfileWABADisable: {
      wabaId: number;
      whatsappEsProfileInternalNames: string[];
    };
  }
>;

type TChannelProfileReinstateNotification = TGenericNotification<
  EWebNotificationType.ChannelProfileWABAReinstate,
  {
    whatsAppESProfileWABAReinstate: {
      wabaId: number;
      whatsappEsProfileInternalNames: string[];
    };
  }
>;

type TChannelProfileMarketingMessagesApiTermsSignedNotification = TGenericNotification<
  EWebNotificationType.ChannelProfileWABAMarketingMessagesApiTermsSigned,
  {
    whatsAppESProfileWABAMarketingMessagesApiTermsSigned: {
      wabaId: number;
      whatsappEsProfileInternalNames: string[];
    };
  }
>;

type TChannelProfileMaxConnectedNotification = TGenericNotification<
  EWebNotificationType.ChannelProfileMaxConnected,
  {
    maxConnected: {
      tenantId: number;
      profileId: number;
      occurredAt: string;
      profileName: string;
    };
  }
>;

type TChannelProfileMaxDisconnectedNotification = TGenericNotification<
  EWebNotificationType.ChannelProfileMaxDisconnected,
  {
    maxDisconnected: {
      tenantId: number;
      profileId: number;
      occurredAt: string;
      profileName: string;
    };
  }
>;

type TChannelProfileTelegramConnectedNotification = TGenericNotification<
  EWebNotificationType.ChannelProfileTelegramConnected,
  {
    telegramConnected: {
      tenantId: number;
      profileId: number;
      occurredAt: string;
      profileName: string;
    };
  }
>;

type TChannelProfileTelegramDisconnectedNotification = TGenericNotification<
  EWebNotificationType.ChannelProfileTelegramDisconnected,
  {
    telegramDisconnected: {
      tenantId: number;
      profileId: number;
      occurredAt: string;
      profileName: string;
    };
  }
>;

type TChannelProfileMaxBotRegistrationNotification = TGenericNotification<
  EWebNotificationType.ChannelProfileMaxBotRegistration,
  {
    maxBotRegistration: {
      tenantId: number;
      profileName: string;
      profileId: number;
    };
  }
>;

type TChannelProfileMaxBotSubjectCreatedNotification = TGenericNotification<
  EWebNotificationType.ChannelProfileMaxBotSubjectCreated,
  {
    maxBotSubjectCreated: {
      tenantId: number;
      profileName: string;
      profileId: number;
      subject: string;
      subjectId: number;
      occurredAt: string;
    };
  }
>;

type TMessageMatcherCategoryChangedNotification = TGenericNotification<
  EWebNotificationType.MessageMatcherCategoryChanged,
  {
    messageMatcherCategoryChanged: {
      name: string;
      newCategory: EWhatsAppCategory;
    };
  }
>;

type TMessageMatcherStatusChangedNotification = TGenericNotification<
  EWebNotificationType.MessageMatcherStatusChanged,
  {
    messageMatcher: {
      name: string;
      approveStatus: EApproveStatus;
    };
  }
>;

type TJivoNotification = TGenericNotification<
  EWebNotificationType.JivoDisabled | EWebNotificationType.JivoEnabled,
  {
    updateJivoIntegration: {
      login: string;
    };
  }
>;

type TAgreedChangeTariffRequestNotification = TGenericNotification<
  EWebNotificationType.AgreedChangeTariffRequest,
  {
    agreedChangeTariffRequest: {
      login: string;
      tariffName: string;
    };
  }
>;

type TNotAgreedChangeTariffRequestNotification = TGenericNotification<
  EWebNotificationType.NotAgreedChangeTariffRequest,
  {
    notAgreedChangeTariffRequest: {
      login: string;
      tariffName: string;
    };
  }
>;

type TTenantTariffAcceptedNotification = TGenericNotification<
  EWebNotificationType.TenantTariffAccepted,
  {
    acceptTenantTariff: {
      login: string;
    };
  }
>;

type TPaymentDetailsUpdatedNotification = TGenericNotification<
  EWebNotificationType.PaymentDetailsUpdated,
  {
    updatePaymentDetails: {
      login: string;
    };
  }
>;

type TCompanyDetailsUpdatedNotification = TGenericNotification<
  EWebNotificationType.CompanyDetailsUpdated,
  {
    updateCompanyDetails: {
      login: string;
    };
  }
>;

type TLicenseExpiringNotification = TGenericNotification<
  EWebNotificationType.LicenseExpiring,
  {
    licenseExpiring: {
      expireAt: string;
    };
  }
>;

type TSubscribersUploadNotification = TGenericNotification<
  | EWebNotificationType.SubscribersUploadFailed
  | EWebNotificationType.SubscribersUploadLoading
  | EWebNotificationType.SubscribersUploadPartial
  | EWebNotificationType.SubscribersUploadSuccess,
  {
    subscriberUpload: {
      uploadId: number;
      fileName?: string;
      reportUrl?: string;
      errorReportFileName?: string;
      uploadedInTotal?: number;
      uploadedWithErrors?: number;
    };
  }
>;

type TBroadcastNotification = TGenericNotification<
  EWebNotificationType.BroadcastCancelled | EWebNotificationType.BroadcastCompleted,
  {
    broadcast: {
      id: number;
      name: string;
    };
  }
>;

enum EAutopaymentNotificationStatus {
  AUTO_REPLENISHMENT_ENABLED = 'AUTO_REPLENISHMENT_ENABLED',
  AUTO_REPLENISHMENT_DISABLED = 'AUTO_REPLENISHMENT_DISABLED',
  PAYMENT_SUCCEEDED = 'PAYMENT_SUCCEEDED',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
}

type TStripeAutopaymentNotification = TGenericNotification<
  EWebNotificationType.StripeCheckout,
  {
    checkoutData: TAutopaymentError & {
      status: EAutopaymentNotificationStatus;
      hostedReceiptUrl?: string;
    };
  }
>;

enum EPayKeeperNotificationStatus {
  PAYMENT_SUCCESS = 'PAYMENT_SUCCESS',
  PAYMENT_FAIL = 'PAYMENT_FAIL',
  REFUND_SUCCESS = 'REFUND_SUCCESS',
  RECURRENT_PAYMENT_SUCCESS = 'RECURRENT_PAYMENT_SUCCESS',
  RECURRENT_PAYMENT_FAIL = 'RECURRENT_PAYMENT_FAIL',
  RECURRENT_CARD_BINDING_CREATED = 'RECURRENT_CARD_BINDING_CREATED',
}

type TPayKeeperPaymentNotification = TGenericNotification<
  EWebNotificationType.PaykeeperPayment,
  {
    paymentData: {
      paymentDate: string;
      sum: number;
      status: EPayKeeperNotificationStatus;
    };
  }
>;

type TReport = {
  name: string;
  url?: string;
};

type TMessageReportNotification = TGenericNotification<
  | EWebNotificationType.MessageReportSuccess
  | EWebNotificationType.MessageReportFailed
  | EWebNotificationType.MessageReportLoading,
  {
    messageReport: TReport;
  }
>;

type TFlowMessageReportNotification = TGenericNotification<
  | EWebNotificationType.FlowMessageReportFailed
  | EWebNotificationType.FlowMessageReportLoading
  | EWebNotificationType.FlowMessageReportSuccess,
  {
    flowReport: TReport;
  }
>;

type TWebNotification =
  | TChannelProfilePartnerProfileCreated
  | TChannelProfileQualityStatusChangedNotification
  | TChannelProfileMessagingLimitChangedNotification
  | TChannelProfileQualityScoreChangedNotification
  | TChannelProfileViolationNotification
  | TChannelProfileRestrictionNotification
  | TChannelProfileDisableNotification
  | TChannelProfileReinstateNotification
  | TChannelProfileMarketingMessagesApiTermsSignedNotification
  | TChannelProfileMaxBotRegistrationNotification
  | TChannelProfileMaxBotSubjectCreatedNotification
  | TMessageMatcherCategoryChangedNotification
  | TMessageMatcherStatusChangedNotification
  | TJivoNotification
  | TAgreedChangeTariffRequestNotification
  | TPaymentDetailsUpdatedNotification
  | TNotAgreedChangeTariffRequestNotification
  | TTenantTariffAcceptedNotification
  | TCompanyDetailsUpdatedNotification
  | TLicenseExpiringNotification
  | TSubscribersUploadNotification
  | TBroadcastNotification
  | TStripeAutopaymentNotification
  | TPayKeeperPaymentNotification
  | TMessageReportNotification
  | TFlowMessageReportNotification
  | TChannelProfileMaxConnectedNotification
  | TChannelProfileMaxDisconnectedNotification
  | TChannelProfileTelegramConnectedNotification
  | TChannelProfileTelegramDisconnectedNotification
  | TGenericNotification<
      | EWebNotificationType.CallbackUpdated
      | EWebNotificationType.ApikeyUserUpdated
      | EWebNotificationType.InvoicePaid
    >;

export type {
  TStripeAutopaymentNotification,
  TPayKeeperPaymentNotification,
  TBroadcastNotification,
  TSubscribersUploadNotification,
  TLicenseExpiringNotification,
  TCompanyDetailsUpdatedNotification,
  TPaymentDetailsUpdatedNotification,
  TTenantTariffAcceptedNotification,
  TNotAgreedChangeTariffRequestNotification,
  TAgreedChangeTariffRequestNotification,
  TJivoNotification,
  TMessageMatcherStatusChangedNotification,
  TMessageMatcherCategoryChangedNotification,
  TChannelProfileQualityScoreChangedNotification,
  TChannelProfileMessagingLimitChangedNotification,
  TChannelProfilePartnerProfileCreated,
  TChannelProfileQualityStatusChangedNotification,
  TChannelProfileViolationNotification,
  TChannelProfileRestrictionNotification,
  TChannelProfileDisableNotification,
  TChannelProfileReinstateNotification,
  TChannelProfileMarketingMessagesApiTermsSignedNotification,
  TChannelProfileMaxBotRegistrationNotification,
  TChannelProfileMaxBotSubjectCreatedNotification,
  TChannelProfileMaxConnectedNotification,
  TChannelProfileMaxDisconnectedNotification,
  TChannelProfileTelegramConnectedNotification,
  TChannelProfileTelegramDisconnectedNotification,
  TMessageReportNotification,
  TFlowMessageReportNotification,
  TWebNotification,
  TReport,
};
export {
  EWebNotificationType,
  EAutopaymentNotificationStatus,
  EPayKeeperNotificationStatus,
  EWebNotificationSection,
  NOTIFICATIONS_LIST_PATH,
  UPLOADS_LIST_PATH,
  UPLOADS_TYPES,
  HIDDEN_TYPES,
};
